import { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';

import { SET_CURRENT_PROFILE, SET_UI_MODE } from '../../../http/apollo-queries';

import { NewStrategyPage } from '../../NewStrategy/NewStrategyContext';
import { useAuth } from '../../../context/Auth';
import { useTutorialContext } from '../../../context/Tutorial';
import { useUserStrategies } from '../../../context/UserStrategies';
import { useThemeContext } from '../../../context/Theme';

import { Button, BurgerMenu, Icon, Loading } from '../../UI';
import { DropdownMenu } from '../../form';
import { Navigation } from '../../Navigation/Navigation';
import { TOP_NAV } from '../../../constants/nav';
import { Modal } from '../../Modal';
import ChatButton from '../../WindowWidget/Chat/ChatButton';
import TooltipIcon from '../../InfoTooltip/Tooltip-icon';
import Search from './Search';

import i18n from '../../../i18n';
import { DropdownMenuPropsModel } from '../../form/Dropdown-menu/dropdown-menu-props.model';
import { UpgradeSubscription } from '../../UpgradeSubscription/UpgradeSubscription';
import ExpirationWarning from '../../ExpirationWarning/ExpirationWarning';
import { trackEvent } from '../../../helpers/analytics';
import { logger } from '../../../helpers/logger';
import { NotificationButton } from '../../WindowWidget/Notifications/NotificationButton';

const DefaultNav = () => {
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { t } = useTranslation();

  const { user, refetch } = useAuth();
  const { setTheme, theme } = useThemeContext();

  const {
    isTooltipIconVisible,
    dispatch,
    StrategiesNavButtonTooltip,
    StrategiesTutorial,
  } = useTutorialContext();
  const { draftStrategy, canAddStrategy, strategies } = useUserStrategies();

  const [setUIMode, { loading }] = useMutation(SET_UI_MODE, {
    onError: (err) => {
      logger.error(err);
      toast.error(t('chooseTheme.error'));
    },
    notifyOnNetworkStatusChange: true,
  });

  const [upgradeModalFeature, setUpgradeModalFeature] = useState<
    'PROFILES' | 'STRATEGIES' | ''
  >('');
  const [isPaymentExpiring, setIsPaymentExpiring] = useState(false); // TODO:vaniko calculate based on expiration value

  useEffect(() => {
    canAddStrategy &&
      upgradeModalFeature === 'STRATEGIES' &&
      setUpgradeModalFeature('');
  }, [
    user?.membership?.membershipPlan?.features?.strategies,
    strategies.length,
  ]);

  const [setCurrentProfile, { loading: loadingProfileChange }] = useMutation(
    SET_CURRENT_PROFILE,
    {
      onCompleted: (data) => {
        refetch().then(() => {
          navigate('/strategies');
        });
      },
      onError: (err) => {
        logger.error(err);
        toast.error(t('chooseAccount.error'));
      },
    }
  );

  const profile = user?.profile;
  const switchToMode = theme === 'DARK' ? 'LIGHT' : 'DARK';

  const USER_NAV = useMemo(
    () => ({
      account: () => {
        trackEvent('home_account');
        navigate('/account');
      },
      subscriptions: () => {
        trackEvent('click_subscriptions');
        navigate('/subscriptions');
      },
      ...(user?.profile?.default
        ? {
            payments: () => {
              trackEvent('click_payments');
              navigate('/payments');
            },
          }
        : null),
      support: () => navigate('/support'),
      contacts: () => navigate('/contacts'),
      feedback: () => navigate('/feedback'),
      referFriend: () => navigate('/referral'),
      connectedDevices: () => navigate('/devices'),
      switchUIMode: () => {
        setTheme(switchToMode); // for Optimistic Update
        setUIMode({
          variables: {
            mode: switchToMode,
          },
        });
      },
    }),
    [navigate, setUIMode, switchToMode]
  );

  const keys = Object.keys(USER_NAV);
  const values = Object.values(USER_NAV);

  const handleChangeLanguage = (code: string) =>
    i18n.changeLanguage(code).then(() => logger.log('Switch language:', code));

  const dropdownItems: DropdownMenuPropsModel['dropdownItems'] = [
    ...keys.map((k) =>
      t(`defaultNav.userNav.${k}`, { x: switchToMode.toLocaleLowerCase() })
    ),
    {
      item: t(`defaultNav.userNav.switchAccount`),
      hideItemOnClick: false,
      dropdownItems: [
        ...(user?.profiles.items.map((user) => {
          return (
            <div
              key={user.id}
              className="flex items-center justify-between w-full gap-14"
              onClick={() =>
                setCurrentProfile({
                  variables: {
                    profileId: user.id,
                  },
                })
              }
            >
              <p>{user.name}</p>
              {user.current && <Icon svgName="mark" />}
            </div>
          );
        }) || []),
      ],
      tooltipChildren: {
        element: (
          <div
            className="flex items-center px-4 py-3 gap-2 cursor-pointer border-t border-grey-10 text-black dark:text-white dark:border-grey-80"
            onClick={() =>
              user?.membership?.membershipPlan?.title === 'Arthur'
                ? navigate('/choose-username?newAccount=true')
                : setUpgradeModalFeature('PROFILES')
            }
          >
            <Icon svgName="plus" className="w-3 h-3" />
            Add new account
          </div>
        ),
        placement: 'bottom',
      },
    },
    // FIXME the possibility of changing languages is temporarily suspended
    // {
    //   item: t('defaultNav.userNav.language'),
    //   dropdownItems: LANGUAGES.map(({ code }) => (
    //     <SimpleDropdownItem
    //       key={code}
    //       onClick={() => handleChangeLanguage(code)}
    //       selected={i18n.language}
    //       title={code}
    //     />
    //   )),
    // },
    { item: t('defaultNav.userNav.privacyAndTerms'), withBorder: true },
    t('defaultNav.userNav.logout'),
  ];

  const dropdownItemsData = [
    ...values,
    // uncomment when FIXME
    // null, // NestedDropdown
    null, // NestedDropdown
    () => navigate('/privacy-and-terms'),
    () => navigate('/logout'),
  ];

  const [isModalOpen, setIsModalOpen] = useState(
    (user?.profile?.creationStatus !== 'COMPLETED' && !draftStrategy) ||
      state?.isCreateStrategyModalOpen === true
  );

  useEffect(() => {
    if (state?.isCreateStrategyModalOpen) {
      setIsModalOpen(true);
    }
  }, [state?.isCreateStrategyModalOpen]);

  const handleModal = () => setIsModalOpen(!isModalOpen);

  const handleNewStrategy = () =>
    canAddStrategy ? handleModal() : setUpgradeModalFeature('STRATEGIES');

  return !user?.profile ? (
    <></>
  ) : (
    <header
      className={`
        sticky
        top-0
        z-50
        w-full
        border-opacity-50
        border-grey-30
        bg-white
        dark:bg-black-real
        dark:border-grey-80
      `}
    >
      <StrategiesTutorial />
      <nav
        className={`
        flex
        w-full
        border-opacity-50
        border-grey-30
        bg-white
        dark:bg-grey-90
        dark:text-grey-50
        shadow-md
      `}
      >
        <div
          className={`
          flex
          items-center
          justify-between
          w-full
          min-h-18
          text-black
          px-4
          border-b
          border-transparent
          dark:border-b
          dark:border-grey-70
        `}
        >
          {/* HAMBURGER MENU (SMALL SCREENS) */}
          <BurgerMenu>
            {({ setVisible }) => (
              <div className={'divide-y divide-grey-10 dark:divide-grey-70'}>
                <div>
                  {Object.entries(TOP_NAV).map(([key, path]) => (
                    <div
                      key={key}
                      onClick={() => setVisible(false)}
                      className="w-full"
                    >
                      <Navigation
                        path={path}
                        className="w-full h-full py-4 hover:text-black dark:hover:text-grey-50"
                        // activeClassName="border-b-4 border-primary"
                      >
                        {t('defaultNav.topNav.' + key)}
                      </Navigation>
                    </div>
                  ))}
                </div>
                <div className={'w-full flex justify-center p-4'}>
                  <Button
                    buttonClass="flex gap-1.5 items-center text-black"
                    type="button"
                    variant="primary"
                    onClick={handleNewStrategy}
                  >
                    {t('defaultNav.newStrategy')}
                    <span>+</span>
                  </Button>
                </div>
              </div>
            )}
          </BurgerMenu>

          {/* LOGO */}
          <div className={'py-10 md:py-0 hidden lg:block '} id={'merlinLogo'}>
            <Link to={'/strategies'}>
              <Icon svgName="merlinLogo" className={'dark:text-white'} />
            </Link>
          </div>

          {/* NEW STRATEGY */}
          <Modal
            className="h-full w-full"
            element={<NewStrategyPage closeModal={handleModal} />}
            isFullScreenElement
            isOpen={isModalOpen}
            isClosable={false}
            padding={'none'}
          />

          {/* LINKS */}
          <div className={'md:flex items-center h-full w-full'}>
            <Button
              buttonClass="hidden lg:flex gap-1.5 items-center w-40 text-black mx-auto md:mx-5"
              type="button"
              variant="primary"
              onClick={handleNewStrategy}
            >
              {t('defaultNav.newStrategy')}
              <span>+</span>
            </Button>
            <div
              className={`
              hidden
              lg:grid
              lg:grid-cols-${Object.keys(TOP_NAV).length}
              lg:ml-1
              h-full
              border-r
              dark:border-grey-70
              border-grey-10
            `}
            >
              {Object.entries(TOP_NAV).map(([key, path]) => (
                <div
                  key={key}
                  className="w-full sm:w-24 lg:w-32 xl:w-40 border-l border-grey-10 dark:border-grey-70"
                >
                  <Navigation
                    path={path}
                    className="w-full h-full py-4 hover:text-black dark:hover:text-grey-50"
                    activeClassName="border-b-4 border-primary"
                  >
                    <>
                      {t('defaultNav.topNav.' + key)}
                      {pathname === '/strategies' && path === '/strategies' && (
                        <StrategiesNavButtonTooltip
                          tooltipPopupClassname="w-52"
                          tooltipClassname="ml-1"
                        />
                      )}
                    </>
                  </Navigation>
                </div>
              ))}
            </div>

            <div className="h-full lg:block bg-white flex-shrink">
              <Search />
            </div>
          </div>

          {/* ACTION ICONS */}
          <TooltipIcon
            tooltipIconColor={`${isTooltipIconVisible ? 'black' : 'grey-50'}`}
            tooltipIconClassname={`
            hidden
            lg:flex
            border-l
            border-r
            border-grey-30
            dark:border-grey-70
            px-6
            h-full
          `}
            onClick={() =>
              dispatch({ isTooltipIconVisible: !isTooltipIconVisible })
            }
          />

          <div className="flex text-white gap-4 ml-6 items-center justify-center">
            <NotificationButton />
            <ChatButton />
            <DropdownMenu
              hideOnMouse
              tooltipChildren={{
                element: (
                  <div className="flex border-b border-grey-10 p-3 items-center text-black dark:border-grey-70">
                    {loading || loadingProfileChange ? (
                      <div className="flex w-full justify-center p-2">
                        <Loading status={true} size="small" />
                      </div>
                    ) : (
                      <>
                        <img
                          src={profile?.avatar?.size?.avatarMedium}
                          alt="user avatar"
                          className="w-12 h-12 object-cover rounded-full"
                        />
                        <div className="ml-3 flex flex-col justify-center">
                          <p className="text-base font-medium dark:text-white">
                            {profile?.name}
                          </p>
                          <p className="flex justify-center rounded-full font-medium bg-black text-xxs text-primary px-2 mt-0.5">
                            {user.membership.membershipPlan.title}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ),
                placement: 'top',
              }}
              dropdownIconClassName="w-12 items-center"
              dropdownTooltipClassName="bg-white mt-2 border-solid border-grey-10 border shadow-md"
              dropdownItems={dropdownItems}
              dropdownItemsData={dropdownItemsData}
              handleClick={(label, action) => {
                if (typeof action === 'function') action();
              }}
              dropdownArrowClassName="ml-1"
              buttonClassName={'w-64'}
              showArrow
            >
              <div className={'rounded-full w-6 h-6 bg-grey-10 relative'}>
                {user?.profile?.avatar?.size?.avatarSmall && (
                  <img
                    src={user?.profile?.avatar?.size?.avatarSmall}
                    alt={user?.profile?.avatar?.filename}
                    className={'rounded-full w-6 h-6 object-cover'}
                  />
                )}
                <div
                  className={
                    'bg-black flex items-center justify-center w-3.5 h-3.5 text-xxs rounded-full absolute -right-1 -top-2'
                  }
                >
                  {user.membership.membershipPlan.title.substring(0, 1)}
                </div>
              </div>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {isPaymentExpiring && <ExpirationWarning />}

      <UpgradeSubscription
        isOpen={upgradeModalFeature.length > 0}
        bannerClassName={
          upgradeModalFeature === 'STRATEGIES' ? 'px-5 pt-5' : ''
        }
        textVars={{
          number:
            upgradeModalFeature === 'STRATEGIES'
              ? user?.membership?.membershipPlan?.features.strategies || 0
              : user.membership.membershipPlan.features.profiles,
          plan: user?.membership?.membershipPlan?.title || '',
        }}
        onCloseClick={() => {
          setUpgradeModalFeature('');
        }}
        feature={upgradeModalFeature}
        viewMode={upgradeModalFeature === 'STRATEGIES' ? 'banner' : 'modal'}
      />
    </header>
  );
};

export default DefaultNav;
